import { type CampaignColor, type IdeaRunWindow, POST_PLATFORMS, type Post, type PostPlatform } from '../api.ts';
import { localClock, localTime, since } from '../schedule.ts';
import type { BoardCard } from './model.ts';

/**
 * What a card says about itself (US-015): its one line (Design reference
 * § Card lines), its due chip and post chips (§ Due dates), and the colour of
 * its campaign edge (§ Campaign colours). Plain functions over the card and a
 * clock, so the server's copy in `admin-cli.ts` can be checked against this
 * one row for row; only the times differ (the browser's zone here, UTC there).
 */

/** One card line: the words, whether a small `Loader` turns beside them, and a tooltip. */
export interface CardLine {
  readonly text: string;
  readonly loader: boolean;
  readonly title?: string;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/** `Thu 16 Oct 09:00` in this browser's zone: a post's moment, short enough for a chip. */
export function localMoment(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return (
    `${WEEKDAYS[date.getDay()] ?? ''} ${String(date.getDate())} ${MONTHS[date.getMonth()] ?? ''} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

/** `Thu 16 Oct, 09:00`: a post's moment in a posts panel row (US-024); `localMoment` with a comma. */
export function postMoment(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return (
    `${WEEKDAYS[date.getDay()] ?? ''} ${String(date.getDate())} ${MONTHS[date.getMonth()] ?? ''}, ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

/** `16 Oct` for a `YYYY-MM-DD` publish-by date, read as a calendar day (never as UTC midnight). */
export function shortDay(day: string): string {
  const [, month = 0, date = 0] = day.split('-').map(Number);
  return `${String(date)} ${MONTHS[month - 1] ?? ''}`;
}

/** Today in this browser's zone, as `YYYY-MM-DD`, `days` ahead. */
function localDay(now: number, days = 0): string {
  const date = new Date(now);
  date.setDate(date.getDate() + days);
  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** A platform's name; an Other post goes by its label. */
export function postPlatformLabel(platform: PostPlatform, label: string | null = null): string {
  if (platform === 'other' && label !== null) return label;
  return POST_PLATFORMS.find((candidate) => candidate.id === platform)?.label ?? platform;
}

function line(text: string, loader = false): CardLine {
  return { text, loader };
}

/** What is happening to a card, in one line. The first matching row of § Card lines wins. */
export function cardLine(card: BoardCard, now: number = Date.now()): CardLine {
  if (card.type === 'idea') {
    const { idea } = card;
    switch (idea.status) {
      case 'raw':
        return line('Waiting to be worked out');
      case 'working':
        return line('Claude is working it out', true);
      case 'drafted':
        return line('Ready for your decision');
      case 'failed':
        return { text: 'Could not be worked out', loader: false, ...(idea.error === null ? {} : { title: idea.error }) };
      default:
        return line(idea.status);
    }
  }
  const { content } = card;
  switch (content.status) {
    case 'pending':
      if (content.lastError !== null) return line('Setup failed');
      if (content.containerId === null) return line('Setting up', true);
      if (content.planParses) return line('Plan written — mark it ready');
      switch (content.planningState) {
        case 'terminal':
          return line('Planning in the terminal');
        case 'thinking':
          return line('Claude is planning', true);
        case 'your-turn':
          return line('Claude asked you something');
        case 'decided':
          return line('Claude decided on autopilot');
        case 'failed':
          return line('Planning stopped');
        default:
          return line('Not planned yet');
      }
    case 'ready':
      if (content.queuePosition !== null) return line(`Queued #${String(content.queuePosition)}`);
      if (content.scheduleMissed) return line('Missed its scheduled start');
      if (content.scheduledStartAt !== null) return line(`Starts ${localTime(content.scheduledStartAt)}`);
      return line('Ready to start');
    case 'producing':
      if (content.autoReview !== null) {
        return line(`Claude is revising, round ${String(content.autoReview.round)}`, true);
      }
      return line(
        `Part ${String(Math.min(content.steps.done + 1, content.steps.total))} of ${String(content.steps.total)}`,
        true,
      );
    case 'waiting':
      return line(content.waitingUntil === null ? 'On hold' : `On hold until ${localClock(content.waitingUntil)}`);
    case 'approval':
      if (content.sketchState === 'preparing') return line('Claude is preparing the scenes', true);
      if (content.sketchState === 'adjusting') return line('Claude is adjusting the scenes', true);
      return line(content.gate === 'script' ? 'Approve the narration' : 'Approve the scenes');
    case 'failed':
      return line(content.failureStage === null ? 'Failed' : `Failed at ${content.failureStage}`);
    case 'review':
      if (content.revising) return line('Claude is making your changes', true);
      if (content.autoReview?.phase === 'reviewing') {
        return line(`Claude is checking it, round ${String(content.autoReview.round)}`, true);
      }
      if (content.autoReview !== null) {
        return line(`Claude is revising, round ${String(content.autoReview.round)}`, true);
      }
      return line(`Version ${String(content.currentVersion)} is ready for you`);
    case 'delivered':
      if (content.revising) return line('Claude is making your changes', true);
      if (card.column === 'posted') {
        return line(content.placedAt === null ? 'Posted' : `Posted ${since(content.placedAt, now)}`);
      }
      if (card.column === 'scheduled' && content.posts.next !== null) {
        const { next } = content.posts;
        return line(`Next: ${postPlatformLabel(next.platform)} · ${localMoment(next.scheduledAt)}`);
      }
      return line(content.approvedAt === null ? 'Delivered' : `Delivered ${since(content.approvedAt, now)}`);
    case 'rejected':
      return line(content.rejectedAt === null ? 'Rejected' : `Rejected ${since(content.rejectedAt, now)}`);
    default:
      return line(content.status);
  }
}

/** A chip's look: one of the badge tones, a text and a tooltip. */
export interface Chip {
  readonly tone: 'neutral' | 'wait' | 'danger';
  readonly text: string;
  readonly title?: string;
}

/**
 * The publish-by chip, or `null` without a date or once the content is
 * placed: overdue in red, within two days in amber, later in grey.
 */
export function dueChip(dueOn: string | null, placed: boolean, now: number = Date.now()): Chip | null {
  if (dueOn === null || placed) return null;
  if (dueOn < localDay(now)) return { tone: 'danger', text: `Overdue · ${shortDay(dueOn)}` };
  return { tone: dueOn <= localDay(now, 2) ? 'wait' : 'neutral', text: `Due ${shortDay(dueOn)}` };
}

/**
 * A dated idea's run window (the ad bible US-019), shown from well before it
 * opens so the idea gets made in time: amber within a week of opening and
 * while open, gone once it has closed.
 */
export function runWindowChip(window: IdeaRunWindow | null, now: number = Date.now()): Chip | null {
  if (window === null) return null;
  const today = localDay(now);
  if (window.to < today) return null;
  const range = `${shortDay(window.from)} – ${shortDay(window.to)}`;
  if (window.from > today) {
    return {
      tone: window.from <= localDay(now, 7) ? 'wait' : 'neutral',
      text: `Runs ${range}`,
      title: `Run window opens ${shortDay(window.from)}: ${window.label}`,
    };
  }
  return { tone: 'wait', text: `Running · until ${shortDay(window.to)}`, title: `Run window ${range}: ${window.label}` };
}

const AT_RISK_MS = 48 * 60 * 60 * 1000;

/**
 * One post as a chip: "LinkedIn · Thu 16 Oct 09:00", with a ✓ once posted.
 * Due (planned, its time gone) is red; at risk (the content is not delivered
 * and the time is within 48 hours) is amber.
 */
export function postChip(post: Post, delivered: boolean, now: number = Date.now()): Chip & { readonly posted: boolean } {
  const platform = postPlatformLabel(post.platform, post.label);
  const at = post.status === 'posted' ? post.postedAt : post.scheduledAt;
  const text = at === null ? platform : `${platform} · ${localMoment(at)}`;
  const posted = post.status === 'posted';
  if (posted || post.scheduledAt === null) return { tone: 'neutral', text, posted };
  const ms = new Date(post.scheduledAt).getTime() - now;
  if (ms <= 0) return { tone: 'danger', text, posted };
  if (!delivered && ms <= AT_RISK_MS) return { tone: 'wait', text, title: 'Not delivered yet', posted };
  return { tone: 'neutral', text, posted };
}

/** The CSS variable a campaign's colour is drawn with (§ Campaign colours). */
export const CAMPAIGN_COLOR_VAR: Record<CampaignColor, string> = {
  blue: 'var(--color-blue-400)',
  teal: 'var(--color-teal-400)',
  green: 'var(--color-green-400)',
  amber: 'var(--color-amber-400)',
  red: 'var(--color-red-400)',
  purple: 'var(--color-purple-400)',
  gray: 'var(--color-ink-300)',
};

